/** User preference. `null` means "no explicit choice" — follow the OS. */
export type MotionPreference = boolean | null;

export const MOTION_STORAGE_KEY = 'numtable.motion';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return window.matchMedia(REDUCE_QUERY).matches;
}

/** What is actually applied right now: true = animations run. */
export function resolveMotion(preference: MotionPreference): boolean {
  return preference === null ? !prefersReducedMotion() : preference;
}

/** Reads the preference the inline script in index.html already looked at. */
export function readStoredMotion(): MotionPreference {
  try {
    const stored = localStorage.getItem(MOTION_STORAGE_KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // Storage can be unavailable (Safari private mode). Fall through.
  }
  return null;
}

export function storeMotion(preference: MotionPreference): void {
  try {
    if (preference === null) localStorage.removeItem(MOTION_STORAGE_KEY);
    else localStorage.setItem(MOTION_STORAGE_KEY, preference ? 'on' : 'off');
  } catch {
    // Preference simply won't survive a reload. Not worth failing the UI.
  }
}

/**
 * Writes html[data-motion] — src/styles/tokens.css derives --motion and every
 * --dur-* from it. `null` clears the attribute so the prefers-reduced-motion
 * media query supplies the default, exactly like readStoredMotion above.
 */
export function applyMotion(preference: MotionPreference): boolean {
  const resolved = resolveMotion(preference);
  if (preference === null) delete document.documentElement.dataset['motion'];
  else document.documentElement.dataset['motion'] = preference ? 'on' : 'off';
  return resolved;
}

/** Subscribes to OS motion-preference changes. Only meaningful while following the system. */
export function watchSystemMotion(onChange: () => void): () => void {
  const mq = window.matchMedia(REDUCE_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/**
 * The one required read-point for JS-driven (non-CSS-transition) animation —
 * no component hand-rolls this differently. CSS transitions/animations
 * should prefer var(--dur-*) directly instead of branching on this.
 */
export function readMotionEnabled(): boolean {
  return getComputedStyle(document.documentElement).getPropertyValue('--motion').trim() !== '0';
}
